import { useCallback, useRef, useState } from 'react';
import { apiClient } from '../api/client';
import type { ExportJob, ImportJob } from '../api/types';
import { useJobPolling } from './useJobPolling';

/**
 * Import/Export Dialog
 *
 * Project bundle export/import UI (doc 09 §4): canonical JSON export,
 * `.dgpack` bundle export, and bundle import with a dry-run preview step
 * before committing. Polls the corresponding job's status endpoint until
 * it settles, rather than assuming synchronous completion — export/import
 * both run as queued jobs on the backend (doc 09 §2-3).
 *
 * Styled with plain Bootstrap/Tabler utility classes to match the rest of
 * this codebase's panels (no external component library — see Inspector.tsx
 * and ProjectsView.tsx for the same convention).
 */

interface ImportExportDialogProps {
    /** Omit for a dashboard-level "Import as new project" dialog with no export tab. */
    projectUuid?: string;
    /** When set, "Restore" mode is offered and targets this project. */
    restoreIntoProjectUuid?: string;
    /** When set, the import tab is locked to "subtree" mode targeting this graph (doc 09 §3 "Import bundle here..."), and the export tab is hidden. */
    targetGraphUuid?: string;
    /** Called once a non-dry-run import job reaches 'completed'. */
    onImportComplete?: () => void;
    onClose: () => void;
}

export function ImportExportDialog({ projectUuid, restoreIntoProjectUuid, targetGraphUuid, onImportComplete, onClose }: ImportExportDialogProps) {
    const showExportTab = !!projectUuid && !targetGraphUuid;
    const [tab, setTab] = useState<'export' | 'import'>(showExportTab ? 'export' : 'import');

    return (
        <div
            className="modal d-block"
            style={{ background: 'rgba(0,0,0,0.5)' }}
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">{targetGraphUuid ? 'Import Bundle Here' : 'Import / Export'}</h5>
                        <button type="button" className="btn-close" onClick={onClose} />
                    </div>

                    <div className="modal-body">
                        {showExportTab && (
                            <ul className="nav nav-tabs mb-3">
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${tab === 'export' ? 'active' : ''}`}
                                        onClick={() => setTab('export')}
                                    >
                                        Export
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${tab === 'import' ? 'active' : ''}`}
                                        onClick={() => setTab('import')}
                                    >
                                        Import
                                    </button>
                                </li>
                            </ul>
                        )}

                        {tab === 'export' && projectUuid ? (
                            <ExportPanel projectUuid={projectUuid} />
                        ) : (
                            <ImportPanel
                                restoreIntoProjectUuid={restoreIntoProjectUuid}
                                targetGraphUuid={targetGraphUuid}
                                onImportComplete={onImportComplete}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function ExportPanel({ projectUuid }: { projectUuid: string }) {
    const [job, setJob] = useState<ExportJob | null>(null);
    const [error, setError] = useState<string | null>(null);

    const refetch = useCallback(() => apiClient.getExport(job!.uuid), [job]);
    useJobPolling(job, refetch, setJob);

    const start = async (kind: 'json' | 'bundle') => {
        setError(null);
        setJob(null);
        try {
            const created = kind === 'json'
                ? await apiClient.exportProjectJson(projectUuid)
                : await apiClient.exportProjectBundle(projectUuid);
            setJob(created);
        } catch (e) {
            setError((e as Error).message);
        }
    };

    return (
        <div>
            <p className="text-muted small">
                A canonical JSON export is the raw <code>dreamgraph/1</code> schema — useful for
                diffing or feeding a CI pipeline. A bundle (<code>.dgpack</code>) additionally packs
                every referenced asset file, so it's the one to use for backup, transfer, or sharing.
            </p>

            <div className="d-flex gap-2 mb-3">
                <button className="btn btn-outline-secondary" onClick={() => start('json')}>
                    Export JSON
                </button>
                <button className="btn btn-primary" onClick={() => start('bundle')}>
                    Export Bundle (.dgpack)
                </button>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            {job && (
                <div className="card p-3">
                    <div className="d-flex justify-content-between align-items-center">
                        <span>
                            Status: <strong>{job.status}</strong>
                        </span>
                        {(job.status === 'pending' || job.status === 'processing') && (
                            <span className="spinner-border spinner-border-sm" />
                        )}
                    </div>

                    {job.status === 'completed' && job.download_url && (
                        <a href={job.download_url} className="btn btn-sm btn-success mt-2" download>
                            Download
                        </a>
                    )}

                    {job.status === 'failed' && (
                        <div className="alert alert-danger mt-2 mb-0">{job.error}</div>
                    )}

                    {!!job.warnings?.length && (
                        <ul className="mt-2 mb-0 small text-warning-emphasis">
                            {job.warnings.map((w, i) => (
                                <li key={i}>{w}</li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}

function ImportPanel({
    restoreIntoProjectUuid,
    targetGraphUuid,
    onImportComplete,
}: {
    restoreIntoProjectUuid?: string;
    /** When set, "Subtree" mode is offered and merges into this graph (doc 09 §3 "Import bundle here..."). */
    targetGraphUuid?: string;
    onImportComplete?: () => void;
}) {
    const [mode, setMode] = useState<'new' | 'restore' | 'subtree'>(targetGraphUuid ? 'subtree' : 'new');
    const [file, setFile] = useState<File | null>(null);
    const [job, setJob] = useState<ImportJob | null>(null);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const refetch = useCallback(() => apiClient.getImport(job!.uuid), [job]);
    const handleUpdate = useCallback(
        (updated: ImportJob) => {
            setJob(updated);
            if (!updated.dry_run && updated.status === 'completed') {
                onImportComplete?.();
            }
        },
        [onImportComplete],
    );
    useJobPolling(job, refetch, handleUpdate);

    const runImport = async (dryRun: boolean) => {
        if (!file) return;
        setError(null);
        try {
            const created = mode === 'subtree' && targetGraphUuid
                ? await apiClient.importGraphBundle(targetGraphUuid, file, dryRun)
                : await apiClient.importProjectBundle(file, mode as 'new' | 'restore', {
                      dryRun,
                      projectUuid: mode === 'restore' ? restoreIntoProjectUuid : undefined,
                  });
            setJob(created);
        } catch (e) {
            setError((e as Error).message);
        }
    };

    const report = job?.report;
    const isDryRunPreview = job?.dry_run && job.status === 'completed';

    return (
        <div>
            <p className="text-muted small">
                Upload a <code>.dgpack</code> bundle. We'll validate it and show a preview of what
                will be created or reused before anything is written — nothing changes until you
                confirm.
            </p>

            <div className="mb-3">
                <label className="form-label small">File</label>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".dgpack,application/zip"
                    className="form-control form-control-sm"
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
            </div>

            <div className="mb-3">
                <label className="form-label small d-block">Mode</label>
                {targetGraphUuid ? (
                    <div className="text-muted small">
                        <i className="ti ti-git-merge me-1" />
                        Will merge as a new container in the currently open graph. Entities,
                        variables, and templates already in this project are reused when they
                        match by name; a fresh copy is created otherwise.
                    </div>
                ) : (
                    <div className="btn-group btn-group-sm" role="group">
                        <input
                            type="radio"
                            className="btn-check"
                            id="mode-new"
                            checked={mode === 'new'}
                            onChange={() => setMode('new')}
                        />
                        <label className="btn btn-outline-secondary" htmlFor="mode-new">
                            New project
                        </label>

                        {restoreIntoProjectUuid && (
                            <>
                                <input
                                    type="radio"
                                    className="btn-check"
                                    id="mode-restore"
                                    checked={mode === 'restore'}
                                    onChange={() => setMode('restore')}
                                />
                                <label className="btn btn-outline-secondary" htmlFor="mode-restore">
                                    Restore over this project
                                </label>
                            </>
                        )}
                    </div>
                )}
            </div>

            <button className="btn btn-outline-secondary btn-sm me-2" disabled={!file} onClick={() => runImport(true)}>
                Preview (dry run)
            </button>

            {error && <div className="alert alert-danger mt-3">{error}</div>}

            {job && (
                <div className="card p-3 mt-3">
                    <div className="d-flex justify-content-between align-items-center">
                        <span>
                            Status: <strong>{job.status}</strong> {job.dry_run && '(dry run)'}
                        </span>
                        {(job.status === 'pending' || job.status === 'processing') && (
                            <span className="spinner-border spinner-border-sm" />
                        )}
                    </div>

                    {job.status === 'failed' && (
                        <div className="alert alert-danger mt-2 mb-0">{job.error}</div>
                    )}

                    {report && (
                        <div className="mt-2">
                            {!!report.errors?.length && (
                                <div className="alert alert-danger mb-2">
                                    {report.errors.map((e, i) => <div key={i}>{e}</div>)}
                                </div>
                            )}

                            {report.creates && (
                                <table className="table table-sm mb-2">
                                    <thead>
                                        <tr>
                                            <th>Will create</th>
                                            <th>Count</th>
                                            <th>Reused (matched existing)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Object.entries(report.creates).map(([key, count]) => (
                                            <tr key={key}>
                                                <td>{key}</td>
                                                <td>{count}</td>
                                                <td>{report.reused?.[key] ?? 0}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}

                            {!!report.warnings?.length && (
                                <ul className="small text-warning-emphasis mb-2">
                                    {report.warnings.map((w, i) => <li key={i}>{w}</li>)}
                                </ul>
                            )}

                            {isDryRunPreview && !report.errors?.length && (
                                <button className="btn btn-success btn-sm" onClick={() => runImport(false)}>
                                    Confirm Import
                                </button>
                            )}

                            {!job.dry_run && job.status === 'completed' && (
                                <div className="alert alert-success mb-0">Import complete.</div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
